import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';

const empty = { name: '', description: '' };

export default function Lists() {
  const [lists, setLists] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [manage, setManage] = useState(null);

  async function refresh() {
    const r = await Admin.lists.list();
    setLists(r.lists);
  }
  useEffect(() => { refresh(); }, []);

  function openCreate() { setForm(empty); setErr(null); setModal('create'); }
  function openEdit(l) {
    setForm({ name: l.name, description: l.description || '' });
    setErr(null);
    setModal({ id: l.id });
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (modal === 'create') await Admin.lists.create(form);
      else await Admin.lists.update(modal.id, form);
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function remove(id) { await Admin.lists.remove(id); await refresh(); }

  return (
    <div>
      <PageHeader
        eyebrow="Audience"
        title="Lists"
        description="Group contacts into reusable audiences for campaigns."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New list</button>}
      />

      {lists.length === 0 ? (
        <EmptyState onAction={openCreate} label="Create a list" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => (
            <div key={l.id} className="card flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-xl">{l.name}</h3>
                <span className="chip-neutral numeric">{l.contactCount || 0}</span>
              </div>
              {l.description && <p className="text-sm text-ink-2 mt-2 line-clamp-3">{l.description}</p>}
              <div className="mt-auto pt-5 flex flex-wrap gap-2">
                <button className="btn-outline btn-xs" onClick={() => setManage(l)}>Members</button>
                <button className="btn-ghost btn-xs" onClick={() => openEdit(l)}>Edit</button>
                <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(l)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New' : 'Edit'}
        title={modal === 'create' ? 'Create list' : form.name || 'Edit list'}
        footer={
          <>
            <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('list-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="list-form" onSubmit={save} className="space-y-4">
          <FormField label="Name" required hint="e.g. Donors, A-team parents, Alumni">
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
        </form>
      </Modal>

      {manage && <ManageMembers list={manage} onClose={() => { setManage(null); refresh(); }} />}

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete "${confirm?.name}"?`}
        message={`Contacts in this list will not be deleted — they'll just lose their membership in this list.`}
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

function ManageMembers({ list, onClose }) {
  const [members, setMembers] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState('');

  async function refresh() {
    const [m, all] = await Promise.all([Admin.lists.members(list.id), Admin.contacts.list()]);
    setMembers(m.contacts);
    setAllContacts(all.contacts);
  }
  useEffect(() => { refresh(); }, [list.id]);

  async function addPicked() {
    if (!picked.length) return;
    await Admin.lists.addMembers(list.id, picked);
    setPicked([]); setAdding(false); setSearch('');
    await refresh();
  }
  async function removeOne(contactId) {
    await Admin.lists.removeMember(list.id, contactId);
    await refresh();
  }

  const memberIds = new Set(members.map((m) => m.id));
  const candidates = allContacts
    .filter((c) => !memberIds.has(c.id))
    .filter((c) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [c.email, c.firstName, c.lastName].some((v) => v?.toLowerCase().includes(q));
    });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={`${members.length} member${members.length === 1 ? '' : 's'}`}
      title={list.name}
      size="lg"
      footer={
        adding ? (
          <>
            <button type="button" onClick={() => { setAdding(false); setPicked([]); }} className="btn-ghost">Cancel</button>
            <button type="button" onClick={addPicked} className="btn-primary" disabled={!picked.length}>
              Add {picked.length || ''}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className="btn-ghost">Done</button>
            <button type="button" onClick={() => setAdding(true)} className="btn-primary">+ Add contacts</button>
          </>
        )
      }
    >
      {adding ? (
        <div>
          <input
            autoFocus
            className="input mb-3"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-[50vh] overflow-y-auto border border-line rounded-md divide-y divide-line">
            {candidates.length === 0 && <div className="p-6 text-center text-sm text-ink-3 italic font-display">No contacts to add.</div>}
            {candidates.map((c) => {
              const on = picked.includes(c.id);
              return (
                <label key={c.id} className={`flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer ${on ? 'bg-brand/5' : 'hover:bg-line-soft/50'}`}>
                  <input type="checkbox" className="check" checked={on} onChange={() => setPicked((p) => on ? p.filter((x) => x !== c.id) : [...p, c.id])} />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium truncate block">{c.email}</span>
                    {(c.firstName || c.lastName) && <span className="text-ink-2 text-xs">{[c.firstName, c.lastName].filter(Boolean).join(' ')}</span>}
                  </span>
                  {!c.marketingOptIn && <span className="chip-neutral">no opt-in</span>}
                </label>
              );
            })}
          </div>
        </div>
      ) : members.length === 0 ? (
        <div className="py-12 text-center text-ink-3 italic font-display">No contacts in this list yet.</div>
      ) : (
        <div className="divide-rule border border-line rounded-md">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{m.email}</div>
                {(m.firstName || m.lastName) && <div className="text-xs text-ink-2">{[m.firstName, m.lastName].filter(Boolean).join(' ')}</div>}
              </div>
              <div className="flex items-center gap-2">
                {!m.marketingOptIn && <span className="chip-neutral">no opt-in</span>}
                <button className="btn-ghost btn-xs text-danger" onClick={() => removeOne(m.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
